import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { api } from '../lib/api';
import AdminAccessExpiry from '../components/admin/AdminAccessExpiry';

const { Title, Text } = Typography;

type AdminMember = {
  id: string;
  userId: string;
  role: 'OWNER' | 'ADMIN' | 'MEMBER';
  joinedAt: string;
  user: {
    id: string;
    email: string;
    name: string | null;
  };
};

type AdminSubscription = {
  id: string;
  status: string;
  seatCount?: number;
  currentPeriodStart?: string | null;
  currentPeriodEnd?: string | null;
  trialStart?: string | null;
  trialEnd?: string | null;
  cancelAtPeriodEnd?: boolean;
  stripeSubscriptionId?: string | null;
  app?: { id: string; name: string };
  price?: { id: string; name: string | null; interval: string; unitAmount: number | null; currency: string };
};

type AdminOrganizationDetail = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  createdAt: string;
  manualInvoicing?: boolean;
  members: AdminMember[];
  subscriptions?: AdminSubscription[];
};

type AdminAppPrice = {
  id: string;
  name: string | null;
  interval: string;
  unitAmount: number | null;
  currency: string;
};

type AdminApp = {
  isCatalogGroup?: boolean;
  parent?: { isCatalogGroup?: boolean } | null;
  isAdminGranted?: boolean;
  id: string;
  name: string;
  slug: string;
  parentId?: string | null;
  isActive?: boolean;
  prices?: AdminAppPrice[];
};

type AddMemberValues = {
  email: string;
  role: 'OWNER' | 'ADMIN' | 'MEMBER';
};

type SeatAssignment = {
  id: string;
  userId: string;
  appId: string;
  assignedAt: string;
  user: {
    id: string;
    email: string;
    name: string | null;
  };
  app: {
    id: string;
    name: string;
    slug: string;
  };
  subscription?: {
    id: string;
    status: string;
  };
};

type SeatInfo = {
  totalSeats: number;
  usedSeats: number;
  availableSeats: number;
};

function toDate(value?: string | null) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDate(value?: string | null) {
  const date = toDate(value);
  return date ? date.toLocaleDateString() : '-';
}

function getRemainingDaysText(value?: string | null) {
  const date = toDate(value);
  if (!date) return '-';
  const msPerDay = 24 * 60 * 60 * 1000;
  const remaining = Math.ceil((date.getTime() - Date.now()) / msPerDay);
  if (remaining < 0) return 'Ended';
  if (remaining === 0) return 'Ends today';
  if (remaining === 1) return '1 day left';
  return `${remaining} days left`;
}

function getAssignedTrialDaysText(start?: string | null, end?: string | null) {
  const startDate = toDate(start);
  const endDate = toDate(end);
  if (!startDate || !endDate) return '-';
  const msPerDay = 24 * 60 * 60 * 1000;
  const total = Math.ceil((endDate.getTime() - startDate.getTime()) / msPerDay);
  if (total <= 0) return '-';
  return `${total} days`;
}

const ACTIVE_PAID_STATUSES = new Set(['ACTIVE', 'PAST_DUE', 'UNPAID']);

function hasNotEnded(value?: string | null) {
  const date = toDate(value);
  return !date || date.getTime() >= Date.now();
}

function isCurrentOrganizationSubscription(subscription: Pick<AdminSubscription, 'status' | 'currentPeriodEnd' | 'trialEnd'>) {
  if (subscription.status === 'TRIAL') {
    return hasNotEnded(subscription.trialEnd || subscription.currentPeriodEnd);
  }
  if (ACTIVE_PAID_STATUSES.has(subscription.status)) {
    return hasNotEnded(subscription.currentPeriodEnd);
  }
  return false;
}

function getSubscriptionStatusTag(subscription: AdminSubscription) {
  if (subscription.status === 'TRIAL' && !isCurrentOrganizationSubscription(subscription)) {
    return { color: 'red', label: 'TRIAL ENDED' };
  }
  if (ACTIVE_PAID_STATUSES.has(subscription.status) && !isCurrentOrganizationSubscription(subscription)) {
    return { color: 'red', label: 'EXPIRED' };
  }
  if (subscription.status === 'ACTIVE') {
    return { color: 'green', label: subscription.status };
  }
  if (subscription.status === 'TRIAL') {
    return { color: 'blue', label: subscription.status };
  }
  return { color: 'default', label: subscription.status };
}

export default function AdminOrganizationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState<AdminOrganizationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [billingSaving, setBillingSaving] = useState(false);
  const [apps, setApps] = useState<AdminApp[]>([]);
  const [seatAssignments, setSeatAssignments] = useState<SeatAssignment[]>([]);
  const [stripeLinkMissingIds, setStripeLinkMissingIds] = useState<Set<string>>(new Set());
  const [seatInfoByApp, setSeatInfoByApp] = useState<Record<string, SeatInfo>>({});
  const [defaultTrialDays, setDefaultTrialDays] = useState<number | undefined>();
  const [form] = Form.useForm<AddMemberValues>();
  const [trialForm] = Form.useForm();
  const [inviteForm] = Form.useForm();
  const [assignForm] = Form.useForm();
  const [seatCountForm] = Form.useForm<{ seatCount: number }>();
  const [assigning, setAssigning] = useState(false);
  const [seatCountSubscription, setSeatCountSubscription] = useState<AdminSubscription | null>(null);

  useEffect(() => {
    if (id) {
      loadOrganization();
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    loadApps();
    loadAdminConfig();
  }, [id]);

  const loadOrganization = async () => {
    try {
      setLoading(true);
      const data = await api.admin.getOrganization(id!);
      setOrganization(data.organization);
      const seats = await api.admin.listOrganizationSeats(id!);
      setSeatAssignments(seats.assignments || []);
      await loadSeatInfo(data.organization?.subscriptions || []);
    } catch (error: any) {
      message.error(error.message || 'Failed to load organization');
      navigate('/admin/organizations');
    } finally {
      setLoading(false);
    }
  };

  const loadSeatInfo = async (subscriptions: Array<AdminSubscription>) => {
    if (!id) return;
    const appIds = subscriptions
      .filter((sub: any) => isCurrentOrganizationSubscription(sub))
      .map((sub) => sub.app?.id)
      .filter((appId): appId is string => Boolean(appId));
    const uniqueAppIds = Array.from(new Set(appIds));
    const entries = await Promise.all(
      uniqueAppIds.map(async (appId) => {
        try {
          const seatInfo = await api.admin.getOrganizationSeatAvailability(id, appId);
          return [appId, seatInfo] as const;
        } catch {
          return [appId, null] as const;
        }
      })
    );
    const nextMap: Record<string, SeatInfo> = {};
    for (const [appId, seatInfo] of entries) {
      if (seatInfo) {
        nextMap[appId] = {
          totalSeats: seatInfo.totalSeats ?? 0,
          usedSeats: seatInfo.usedSeats ?? 0,
          availableSeats: seatInfo.availableSeats ?? 0,
        };
      }
    }
    setSeatInfoByApp(nextMap);
  };

  const loadApps = async () => {
    try {
      const data = await api.admin.listApps();
      const appList: AdminApp[] = (data.apps || []).filter(
        (app: AdminApp) => !app.isCatalogGroup && (!app.parentId || app.parent?.isCatalogGroup) && app.isActive !== false
      );
      setApps(appList);
    } catch (error: any) {
      message.error(error.message || 'Failed to load apps');
    }
  };

  const loadAdminConfig = async () => {
    try {
      const data = await api.admin.getConfig();
      if (typeof data?.stripeTrialDays === 'number') {
        setDefaultTrialDays(data.stripeTrialDays);
        trialForm.setFieldsValue({ trialDays: data.stripeTrialDays });
      }
    } catch {
      // Optional config; ignore.
    }
  };

  const handleAddMember = async (values: AddMemberValues) => {
    try {
      if (!id) return;
      setSaving(true);
      const result = await api.admin.addOrganizationMember(id, values.email, values.role);
      const autoAssignedSeats = Number(result?.autoAssignedSeats || 0);
      message.success(
        autoAssignedSeats > 0
          ? `Member added and ${autoAssignedSeats} seat(s) assigned automatically.`
          : 'Member added'
      );
      form.resetFields();
      await loadOrganization();
    } catch (error: any) {
      message.error(error.message || 'Failed to add member');
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (member: AdminMember, role: AdminMember['role']) => {
    try {
      if (!id) return;
      await api.admin.updateOrganizationMemberRole(id, member.userId, role);
      message.success('Role updated');
      await loadOrganization();
    } catch (error: any) {
      message.error(error.message || 'Failed to update role');
    }
  };

  const handleRemoveMember = async (member: AdminMember) => {
    try {
      if (!id) return;
      await api.admin.removeOrganizationMember(id, member.userId);
      message.success('Member removed');
      await loadOrganization();
    } catch (error: any) {
      message.error(error.message || 'Failed to remove member');
    }
  };

  const handleInviteUsers = async (values: { emails?: string }) => {
    try {
      if (!id) return;
      const raw = values.emails || '';
      const emails = raw
        .split(/[\n,;]+/)
        .map((value) => value.trim())
        .filter(Boolean);
      if (emails.length === 0) {
        message.warning('Add at least one email');
        return;
      }
      setSaving(true);
      await api.admin.inviteOrganizationUsers(id, emails);
      message.success('Invitations sent');
      inviteForm.resetFields();
      await loadOrganization();
    } catch (error: any) {
      message.error(error.message || 'Failed to send invitations');
    } finally {
      setSaving(false);
    }
  };

  const handleManualInvoicingToggle = async (nextValue: boolean) => {
    try {
      if (!id) return;
      setBillingSaving(true);
      await api.admin.updateOrganization(id, { manualInvoicing: nextValue });
      message.success('Billing mode updated');
      await loadOrganization();
    } catch (error: any) {
      message.error(error.message || 'Failed to update billing mode');
    } finally {
      setBillingSaving(false);
    }
  };

  const confirmManualInvoicingToggle = (nextValue: boolean) => {
    Modal.confirm({
      title: 'Change billing mode?',
      content:
        'This only changes the organization billing flag. Existing subscriptions stay as-is.',
      okText: 'Change',
      cancelText: 'Keep',
      onOk: () => handleManualInvoicingToggle(nextValue),
    });
  };

  const handleCancelSubscription = async (subscriptionId: string) => {
    try {
      if (!id) return;
      setSaving(true);
      await api.admin.cancelOrganizationSubscription(id, subscriptionId);
      message.success('Subscription canceled');
      await loadOrganization();
    } catch (error: any) {
      message.error(error.message || 'Failed to cancel subscription');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelTrial = async (subscriptionId: string, allowRetrial: boolean) => {
    try {
      if (!id) return;
      setSaving(true);
      const result = await api.admin.cancelOrganizationTrial(id, subscriptionId, allowRetrial);
      if (result?.stripeSubscriptionMissing) {
        setStripeLinkMissingIds((prev) => new Set(prev).add(subscriptionId));
        message.warning('Stripe link missing. Trial was canceled locally.');
      }
      message.success(
        allowRetrial
          ? 'Trial canceled and history reset.'
          : 'Trial canceled.'
      );
      await loadOrganization();
    } catch (error: any) {
      message.error(error.message || 'Failed to cancel trial');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCanceledTrial = async (subscriptionId: string) => {
    try {
      if (!id) return;
      setSaving(true);
      await api.admin.deleteOrganizationCanceledSubscription(id, subscriptionId);
      message.success('Canceled record deleted. Organization can start a new trial.');
      await loadOrganization();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete canceled record');
    } finally {
      setSaving(false);
    }
  };

  const openSeatCountModal = (subscription: AdminSubscription) => {
    setSeatCountSubscription(subscription);
    seatCountForm.setFieldsValue({ seatCount: subscription.seatCount || 1 });
  };

  const handleUpdateSeatCount = async (values: { seatCount: number }) => {
    try {
      if (!id || !seatCountSubscription) return;
      setSaving(true);
      await api.admin.updateOrganizationSubscriptionSeats(
        id,
        seatCountSubscription.id,
        values.seatCount
      );
      message.success('Seat count updated. The subscription period was not changed.');
      setSeatCountSubscription(null);
      seatCountForm.resetFields();
      await loadOrganization();
    } catch (error: any) {
      message.error(error.message || 'Failed to update seat count');
    } finally {
      setSaving(false);
    }
  };

  const handleAssignSeat = async (values: { userId: string; appId: string }) => {
    try {
      if (!id) return;
      setAssigning(true);
      await api.admin.assignOrganizationSeat(id, values.userId, values.appId);
      message.success('Seat assigned');
      assignForm.resetFields();
      await loadOrganization();
    } catch (error: any) {
      message.error(error.message || 'Failed to assign seat');
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassignSeat = async (userId: string, appId: string) => {
    try {
      if (!id) return;
      await api.admin.unassignOrganizationSeat(id, userId, appId);
      message.success('Seat unassigned');
      await loadOrganization();
    } catch (error: any) {
      message.error(error.message || 'Failed to unassign seat');
    }
  };

  const handleAddTrialSubscription = async (values: {
    appId: string;
    priceId?: string;
    quantity?: number;
    trialDays?: number;
    trialOnly?: boolean;
    manual?: boolean;
    expiresAt?: string;
    grantType?: 'STANDARD' | 'TRIAL' | 'MANUAL';
  }) => {
    try {
      if (!id) return;
      setSaving(true);
      await api.admin.createOrganizationSubscription(id, { ...values, manual: values.grantType === 'MANUAL', trialOnly: values.grantType === 'TRIAL', expiresAt: values.grantType === 'MANUAL' && values.expiresAt ? new Date(values.expiresAt).toISOString() : undefined });
      message.success('Access granted');
      trialForm.resetFields();
      if (defaultTrialDays !== undefined) {
        trialForm.setFieldsValue({ trialDays: defaultTrialDays });
      }
      await loadOrganization();
    } catch (error: any) {
      message.error(error.message || 'Failed to create trial subscription');
    } finally {
      setSaving(false);
    }
  };

  const memberColumns: ColumnsType<AdminMember> = [
    {
      title: 'User',
      key: 'user',
      render: (_value, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.user.name || record.user.email}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.user.email}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (value, record) => (
        <Select
          value={value}
          onChange={(next) => handleRoleChange(record, next)}
          options={[
            { value: 'OWNER', label: 'OWNER' },
            { value: 'ADMIN', label: 'ADMIN' },
            { value: 'MEMBER', label: 'MEMBER' },
          ]}
          style={{ width: 140 }}
        />
      ),
    },
    {
      title: 'Joined',
      dataIndex: 'joinedAt',
      key: 'joinedAt',
      render: (value) => new Date(value).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_value, record) => (
        <Popconfirm
          title="Remove member?"
          okText="Remove"
          okButtonProps={{ danger: true }}
          onConfirm={() => handleRemoveMember(record)}
        >
          <Button danger>Remove</Button>
        </Popconfirm>
      ),
    },
  ];

  const subscriptionColumns: ColumnsType<AdminSubscription> = [
    {
      title: 'App',
      key: 'app',
      render: (_value, record) => record.app?.name || '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value, record) => (
        <Space size={4} wrap>
          {(() => {
            const tag = getSubscriptionStatusTag(record);
            return <Tag color={tag.color}>{tag.label}</Tag>;
          })()}
          {value === 'TRIAL' && !record.stripeSubscriptionId && !record.price && (
            <Tag color="cyan">TRIAL-ONLY</Tag>
          )}
          {stripeLinkMissingIds.has(record.id) && (
            <Tag color="warning">Stripe link missing</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Seats',
      dataIndex: 'seatCount',
      key: 'seatCount',
      render: (value) => (typeof value === 'number' ? value : '-'),
    },
    {
      title: 'Price',
      key: 'price',
      render: (_value, record) => record.price?.name || record.price?.interval || '—',
    },
    {
      title: 'Period End',
      key: 'periodEnd',
      render: (_value, record) => formatDate(record.trialEnd || record.currentPeriodEnd),
    },
    {
      title: 'Remaining',
      key: 'remaining',
      render: (_value, record) => getRemainingDaysText(record.trialEnd || record.currentPeriodEnd),
    },
    {
      title: 'Trial Length',
      key: 'trialLength',
      render: (_value, record) =>
        record.status === 'TRIAL'
          ? getAssignedTrialDaysText(record.trialStart || record.currentPeriodStart, record.trialEnd || record.currentPeriodEnd)
          : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_value, record) => {
        const isLocal = Boolean(organization?.manualInvoicing) || !record.stripeSubscriptionId;
        const isStripeManaged = Boolean(record.stripeSubscriptionId);
        const isCurrent = isCurrentOrganizationSubscription(record);
        const expiryButton = !isStripeManaged && ['ACTIVE', 'TRIAL'].includes(record.status) ? <AdminAccessExpiry subscription={record} onSave={(end) => api.admin.updateManualAccessExpiry('organizations', id!, record.id, end)} onUpdated={loadOrganization} /> : null;
        const changeSeatsButton = !isStripeManaged && isCurrent ? (
          <Button size="small" onClick={() => openSeatCountModal(record)}>
            Change Seats
          </Button>
        ) : null;
        if (record.status === 'TRIAL') {
          return (
            <Space>
              {expiryButton}
              {changeSeatsButton}
              <Popconfirm
                title="Cancel trial?"
                onConfirm={() => handleCancelTrial(record.id, false)}
                okText="Cancel Trial"
                okButtonProps={{ danger: true }}
              >
                <Button danger size="small">Cancel Trial</Button>
              </Popconfirm>
              {!isStripeManaged && (
                <Popconfirm
                  title="Cancel trial and allow new trial again?"
                  description="Current trial record will be deleted."
                  onConfirm={() => handleCancelTrial(record.id, true)}
                  okText="Cancel + Reset"
                  okButtonProps={{ danger: true }}
                >
                  <Button size="small">Cancel + Reset</Button>
                </Popconfirm>
              )}
            </Space>
          );
        }
        if (record.status === 'CANCELED') {
          return (
            <Popconfirm
              title="Delete canceled trial record?"
              description="Organization will be eligible for a new trial again."
              onConfirm={() => handleDeleteCanceledTrial(record.id)}
              okText="Delete"
              okButtonProps={{ danger: true }}
            >
              <Button danger size="small">Delete Canceled Trial</Button>
            </Popconfirm>
          );
        }
        const isCancelable = record.status === 'ACTIVE';
        if (!isCancelable || !isCurrent) {
          return <Text type="secondary">—</Text>;
        }
        if (!isLocal) {
          return <Text type="secondary">Stripe managed</Text>;
        }
        return (
          <Space>
            {expiryButton}
            {changeSeatsButton}
            <Popconfirm
              title="Cancel subscription?"
              description="Access will be revoked immediately."
              onConfirm={() => handleCancelSubscription(record.id)}
              okText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button danger size="small">Cancel</Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const seatAssignmentColumns: ColumnsType<SeatAssignment> = [
    {
      title: 'User',
      key: 'user',
      render: (_value, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.user.name || record.user.email}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.user.email}
          </Text>
        </Space>
      ),
    },
    {
      title: 'App',
      key: 'app',
      render: (_value, record) => (
        <Space>
          <Text>{record.app.name}</Text>
          <Tag>{record.app.slug}</Tag>
        </Space>
      ),
    },
    {
      title: 'Assigned',
      dataIndex: 'assignedAt',
      key: 'assignedAt',
      render: (value) => new Date(value).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_value, record) => (
        <Popconfirm
          title="Remove seat assignment?"
          onConfirm={() => handleUnassignSeat(record.userId, record.appId)}
          okText="Remove"
          okButtonProps={{ danger: true }}
        >
          <Button danger size="small">Unassign</Button>
        </Popconfirm>
      ),
    },
  ];

  if (loading) {
    return <Card loading />;
  }

  if (!organization) {
    return null;
  }

  const assignableApps = Array.from(
    new Map(
      (organization.subscriptions || [])
        .filter((sub) => isCurrentOrganizationSubscription(sub) && sub.app?.id)
        .map((sub) => [sub.app!.id, sub.app!])
    ).values()
  );

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2}>{organization.name}</Title>
          <Space>
            <Tag color={organization.slug === 'annea' ? 'gold' : 'blue'}>{organization.slug}</Tag>
            <Text type="secondary">Created {new Date(organization.createdAt).toLocaleDateString()}</Text>
          </Space>
        </div>
        <Space>
          {organization.slug !== 'annea' && (
            <Popconfirm
              title="Delete organization?"
              description="Only possible when there are no active subscriptions."
              onConfirm={async () => {
                try {
                  await api.admin.deleteOrganization(organization.id);
                  message.success('Organization deleted');
                  navigate('/admin/organizations');
                } catch (error: any) {
                  message.error(error.message || 'Failed to delete organization');
                }
              }}
              okText="Delete"
              okButtonProps={{ danger: true }}
            >
              <Button danger>Delete</Button>
            </Popconfirm>
          )}
          <Button onClick={() => navigate('/admin/organizations')}>Back</Button>
        </Space>
      </div>

      <Card title="Organization Overview">
        <Space direction="vertical" size={4}>
          <Text><strong>Name:</strong> {organization.name}</Text>
          <Text><strong>Slug:</strong> {organization.slug}</Text>
          <Text><strong>Description:</strong> {organization.description || '—'}</Text>
          <Space size="small" align="center">
            <Text><strong>Billing:</strong> {organization.manualInvoicing ? 'Manual invoicing' : 'Stripe'}</Text>
            <Switch
              checked={Boolean(organization.manualInvoicing)}
              loading={billingSaving}
              onChange={confirmManualInvoicingToggle}
              checkedChildren="Manual"
              unCheckedChildren="Stripe"
            />
          </Space>
        </Space>
      </Card>

      <Card title="Members">
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Form
            form={form}
            layout="inline"
            onFinish={handleAddMember}
            initialValues={{ role: 'MEMBER' }}
          >
            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Email is required' },
                { type: 'email', message: 'Invalid email' },
              ]}
            >
              <Input placeholder="user@example.com" style={{ width: 260 }} />
            </Form.Item>
            <Form.Item name="role">
              <Select
                style={{ width: 140 }}
                options={[
                  { value: 'OWNER', label: 'OWNER' },
                  { value: 'ADMIN', label: 'ADMIN' },
                  { value: 'MEMBER', label: 'MEMBER' },
                ]}
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={saving}>
                Add Member
              </Button>
            </Form.Item>
          </Form>

          <Table
            rowKey="id"
            dataSource={organization.members || []}
            columns={memberColumns}
            pagination={false}
          />
        </Space>
      </Card>

      <Card title="Invitations">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Form
            form={inviteForm}
            layout="vertical"
            onFinish={handleInviteUsers}
          >
            <Form.Item
              name="emails"
              label="Invite users (comma or newline separated)"
            >
              <Input.TextArea rows={3} placeholder="user1@example.com, user2@example.com" />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={saving}>
              Send Invites
            </Button>
          </Form>
        </Space>
      </Card>

      <Card title="Subscriptions">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {organization.manualInvoicing && (
            <Text type="secondary">
              Manual invoicing is enabled. Subscriptions created here are stored locally without Stripe.
            </Text>
          )}
          <Form
            form={trialForm}
            layout="inline"
            onFinish={handleAddTrialSubscription}
            initialValues={{ trialDays: defaultTrialDays, quantity: 1 }}
          >
            <Form.Item
              name="appId"
              rules={[{ required: true, message: 'App is required' }]}
            >
              <Select
                placeholder="App"
                style={{ width: 220 }}
                options={apps.map((app) => ({ value: app.id, label: app.name }))}
                onChange={(appId) => trialForm.setFieldsValue({ priceId: undefined, grantType: apps.find((app) => app.id === appId)?.isAdminGranted ? 'TRIAL' : 'STANDARD' })}
              />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prev, current) =>
                prev.appId !== current.appId || prev.grantType !== current.grantType
              }
            >
              {({ getFieldValue }) => {
                const appId = getFieldValue('appId') as string | undefined;
                const trialOnly = ['TRIAL', 'MANUAL'].includes(getFieldValue('grantType'));
                const priceOptions = (apps.find((app) => app.id === appId)?.prices || []).map((price) => ({
                  value: price.id,
                  label: `${price.name || price.interval} (${price.currency.toUpperCase()}${price.unitAmount ? ' ' + price.unitAmount / 100 : ''})`,
                }));
                return (
                  <Form.Item
                    name="priceId"
                    rules={trialOnly ? [] : [{ required: true, message: 'Price is required' }]}
                  >
                    <Select
                      placeholder={trialOnly ? 'Not required for local access' : 'Price'}
                      style={{ width: 220 }}
                      options={priceOptions}
                      disabled={trialOnly || priceOptions.length === 0}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(prev, next) => prev.appId !== next.appId}>
              {({ getFieldValue }) => (
                <Form.Item name="grantType" initialValue="STANDARD">
                  <Select style={{ width: 220 }} options={[
                    { value: 'STANDARD', label: 'Standard subscription', disabled: Boolean(apps.find((app) => app.id === getFieldValue('appId'))?.isAdminGranted) },
                    { value: 'TRIAL', label: 'Trial only' },
                    { value: 'MANUAL', label: 'Manual subscription' },
                  ]} />
                </Form.Item>
              )}
            </Form.Item>
            <Form.Item name="quantity">
              <InputNumber min={1} placeholder="Seats" />
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(prev, next) => prev.grantType !== next.grantType}>
              {({ getFieldValue }) => getFieldValue('grantType') === 'MANUAL' ? (
                <Form.Item name="expiresAt" label="Access expiry (optional)">
                  <Input type="datetime-local" />
                </Form.Item>
              ) : null}
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(prev, next) => prev.grantType !== next.grantType}>
              {({ getFieldValue }) => getFieldValue('grantType') !== 'MANUAL' ? (
                <Form.Item name="trialDays"><InputNumber min={1} placeholder="Trial days" /></Form.Item>
              ) : null}
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={saving}>
                Grant Access
              </Button>
            </Form.Item>
          </Form>

        <Table
          rowKey="id"
          dataSource={organization.subscriptions || []}
          columns={subscriptionColumns}
          pagination={false}
        />
        </Space>
      </Card>

      <Card title="Seat Assignments (Super Admin)">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {assignableApps.length > 0 ? (
            <Form form={assignForm} layout="inline" onFinish={handleAssignSeat}>
              <Form.Item
                name="userId"
                rules={[{ required: true, message: 'Member is required' }]}
              >
                <Select
                  placeholder="Select member"
                  style={{ width: 240 }}
                  options={(organization.members || []).map((member) => ({
                    value: member.userId,
                    label: member.user.name || member.user.email,
                  }))}
                />
              </Form.Item>
              <Form.Item
                name="appId"
                rules={[{ required: true, message: 'App is required' }]}
              >
                <Select
                  placeholder="Select app"
                  style={{ width: 240 }}
                  options={assignableApps.map((app) => ({
                    value: app.id,
                    label: app.name,
                  }))}
                />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={assigning}>
                  Assign Seat
                </Button>
              </Form.Item>
            </Form>
          ) : (
            <Text type="secondary">
              No active subscriptions with seats available for assignment.
            </Text>
          )}

          {assignableApps.length > 0 && (
            <Space wrap>
              {assignableApps.map((app) => {
                const seatInfo = seatInfoByApp[app.id];
                const label = seatInfo
                  ? `${seatInfo.usedSeats}/${seatInfo.totalSeats} seats`
                  : 'Seats: —';
                return (
                  <Tag key={app.id} color="blue">
                    {app.name}: {label}
                  </Tag>
                );
              })}
            </Space>
          )}

          <Table
            rowKey="id"
            dataSource={seatAssignments}
            columns={seatAssignmentColumns}
            pagination={false}
          />
        </Space>
      </Card>

      <Modal
        title={`Change seats${seatCountSubscription?.app?.name ? ` — ${seatCountSubscription.app.name}` : ''}`}
        open={Boolean(seatCountSubscription)}
        onCancel={() => {
          setSeatCountSubscription(null);
          seatCountForm.resetFields();
        }}
        onOk={() => seatCountForm.submit()}
        confirmLoading={saving}
        okText="Update Seats"
      >
        <Text type="secondary">
          This changes the capacity of the existing local subscription without changing its billing period.
        </Text>
        <Form form={seatCountForm} layout="vertical" onFinish={handleUpdateSeatCount}>
          <Form.Item
            name="seatCount"
            label="Number of seats"
            rules={[{ required: true, message: 'Seat count is required' }]}
            style={{ marginTop: 16 }}
          >
            <InputNumber min={1} precision={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
